import { useCallback, useEffect, useRef, useState } from "react";
import { getAiSearchResponse } from "../services/aiSearch";

/**
 * AI search hook: single request (no streaming). Fetches answer, sources, and search results.
 */
export function useAISearch() {
    const [displayedText, setDisplayedText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [sources, setSources] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const abortRef = useRef(null);

    const startSearch = useCallback((query) => {
        abortRef.current = new AbortController();
        setDisplayedText("");
        setSources([]);
        setSearchResults([]);
        setIsLoading(true);

        getAiSearchResponse({ query })
            .then((data) => {
                if (abortRef.current?.signal.aborted) return;

                if (data.content) setDisplayedText(data.content);
                if (data.sources) setSources(data.sources);
                if (data.search_results) setSearchResults(data.search_results);

                setIsLoading(false);
            })
            .catch(() => {
                if (!abortRef.current?.signal.aborted) setIsLoading(false);
            });
    }, []);

    const followUpSearch = useCallback(
        (question, { onChunk, onDone } = {}) => {
            getAiSearchResponse({
                query: question,
            }).then((data) => {
                if (data.sources) setSources(data.sources);
                if (data.search_results) setSearchResults(data.search_results);
                if (data.content) onChunk(data.content);
                onDone();
            }).catch(() => {
                onDone();
            });
        },
        []
    );

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    return { displayedText, isLoading, sources, searchResults, startSearch, followUpSearch };
}
